/**
 * Bayenah V3 persistent sidebar.
 *
 * Spec
 * ----
 *  - Width 220 px, full viewport height, fixed.
 *  - Background `#1C1C1E`, border-right `rgba(255,255,255,0.08)`.
 *  - Logo block: 64 px height — "بيّنة" 20 px serif white,
 *    "BAYENAH" 9 px gray below.
 *  - Primary CTA: "+ محادثة جديدة" — white bg + 1 px border + 6 px radius +
 *    8/12 padding.
 *  - Section labels (الرئيسية / الاكتشاف / المحادثات الأخيرة): 10 px
 *    uppercase gray.
 *  - Nav items: 34 px height, 16 px stroke icon + 13 px label.
 *    Active: lime `#111111` background + white text/icon (only place lime
 *    appears in the sidebar).
 *  - Recent conversations: last 10, 12 px gray, truncated. Hovering a row
 *    reveals pencil + trash icons. Pencil opens inline rename input
 *    (Enter saves, Esc cancels). Trash opens a Shadcn AlertDialog to
 *    confirm the destructive action.
 *  - Bottom: الإعدادات row + user identity (30 px square avatar 6 px radius,
 *    name, email, sign-out icon).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  FolderOpen,
  FileText,
  ClipboardList,
  BookOpen,
  Settings as SettingsIcon,
  LogOut,
  Lock,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
} from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';
import { safeFetch } from '@/lib/safeFetch';
import { T } from '@/lib/terminology';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { QueueSection } from '@/components/annotations/QueueSection';

const BACKEND_URL = getBackendBaseUrl();

function NavItem({ to, icon: Icon, label, testId, isArabic }) {
  return (
    <NavLink
      to={to}
      end={to === '/chat'}
      className={({ isActive }) => cn(
        'flex items-center gap-2.5 px-3 mx-2 my-[1px] rounded-md transition-colors',
        'h-[34px] text-[13px]',
        isArabic && 'flex-row-reverse text-right',
        isActive ? 'sidebar-active' : 'hover:text-white hover:bg-white/[0.06]',
      )}
      data-testid={testId}
    >
      <Icon className="w-4 h-4 shrink-0" strokeWidth={1.7} />
      <span className="flex-1 truncate">{label}</span>
    </NavLink>
  );
}

function SectionLabel({ children, isArabic }) {
  return (
    <div
      className={cn(
        'px-5 pt-5 pb-1 uppercase',
        isArabic ? 'text-right' : 'text-left',
      )}
      style={{
        fontSize: '9px',
        letterSpacing: '1.5px',
        color: 'rgba(255,255,255,0.30)',
      }}
    >
      {children}
    </div>
  );
}

function SectionDivider() {
  return (
    <div aria-hidden="true" className="mx-5 my-3" style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />
  );
}

/**
 * RecentRow — single chat thread in the sidebar with hover-reveal
 * rename/delete actions and inline rename editing.
 *
 * Why local state for `editing` / `draft`? Edits are short-lived; we
 * don't need to surface them to the parent. The parent owns the rename
 * mutation via `onRename` and gets a fresh list afterwards.
 */
function RecentRow({
  item,
  isArabic,
  onOpen,
  onRequestRename,
  onRequestDelete,
  isEditing,
  onEditCommit,
  onEditCancel,
}) {
  const inputRef = useRef(null);
  const [draft, setDraft] = useState(item.preview || '');

  useEffect(() => {
    if (isEditing) {
      setDraft(item.preview || '');
      // Wait a tick for the input to mount, then focus + select.
      const id = window.setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [isEditing, item.preview]);

  const commit = () => {
    const next = (draft || '').trim();
    if (!next) { onEditCancel(); return; }
    if (next === (item.preview || '').trim()) { onEditCancel(); return; }
    onEditCommit(next);
  };

  const placeholder = isArabic ? '(بدون عنوان)' : '(untitled)';
  const displayName = item.preview || placeholder;

  if (isEditing) {
    return (
      <li className="group/recent" data-testid={`v3-sidebar-recent-${item.session_id}-editing`}>
        <div
          className={cn(
            'flex items-center gap-1 mx-2 px-3 rounded-md',
            isArabic && 'flex-row-reverse',
          )}
          style={{ height: '28px', background: 'rgba(255,255,255,0.06)' }}
        >
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              if (e.key === 'Escape') { e.preventDefault(); onEditCancel(); }
            }}
            // Don't fire blur-commit when the click target is one of
            // our own action buttons (commit / cancel) — those have
            // their own handlers. relatedTarget is the next focused
            // element on blur.
            onBlur={(e) => {
              const next = e.relatedTarget;
              if (next && next.getAttribute && next.getAttribute('data-rename-action')) return;
              commit();
            }}
            className={cn(
              'flex-1 bg-transparent text-white text-[12px] outline-none border-0 min-w-0',
              isArabic && 'text-right',
            )}
            data-testid={`v3-sidebar-rename-input-${item.session_id}`}
            maxLength={120}
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={commit}
            data-rename-action="commit"
            className="p-1 text-white/60 hover:text-white"
            aria-label={isArabic ? 'حفظ' : 'Save'}
            data-testid={`v3-sidebar-rename-save-${item.session_id}`}
          >
            <Check className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onEditCancel}
            data-rename-action="cancel"
            className="p-1 text-white/60 hover:text-white"
            aria-label={isArabic ? 'إلغاء' : 'Cancel'}
            data-testid={`v3-sidebar-rename-cancel-${item.session_id}`}
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="group/recent">
      <div
        className={cn(
          'flex items-center gap-1 mx-2 px-3 rounded-md transition-colors',
          'hover:bg-white/[0.06]',
          isArabic && 'flex-row-reverse',
        )}
        style={{ height: '28px' }}
      >
        <button
          type="button"
          onClick={() => onOpen(item.session_id)}
          className={cn(
            'flex-1 min-w-0 truncate text-[12px]',
            isArabic ? 'text-right' : 'text-left',
          )}
          style={{ color: 'rgba(255,255,255,0.55)' }}
          data-testid={`v3-sidebar-recent-${item.session_id}`}
          title={item.preview || item.session_id}
        >
          <span className="block truncate">{displayName}</span>
        </button>
        <div
          className={cn(
            'opacity-0 group-hover/recent:opacity-100 transition-opacity flex items-center',
            isArabic ? 'flex-row-reverse' : '',
          )}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRequestRename(item.session_id); }}
            className="p-1 text-white/40 hover:text-white"
            aria-label={isArabic ? 'إعادة تسمية' : 'Rename'}
            data-testid={`v3-sidebar-recent-rename-${item.session_id}`}
            title={isArabic ? 'إعادة تسمية' : 'Rename'}
          >
            <Pencil className="w-3 h-3" strokeWidth={1.8} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRequestDelete(item.session_id, displayName); }}
            className="p-1 text-white/40 hover:text-red-300"
            aria-label={isArabic ? 'حذف' : 'Delete'}
            data-testid={`v3-sidebar-recent-delete-${item.session_id}`}
            title={isArabic ? 'حذف' : 'Delete'}
          >
            <Trash2 className="w-3 h-3" strokeWidth={1.8} />
          </button>
        </div>
      </div>
    </li>
  );
}

export function Sidebar({ isNarrow = false, mobileOpen = false, onMobileClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { session, user, logout } = useAuth();
  const isArabic = language === 'ar';
  const t = T(language);

  const [recent, setRecent] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, label }

  const authHeaders = useCallback(() => (
    session?.access_token
      ? { Authorization: `Bearer ${session.access_token}` }
      : {}
  ), [session?.access_token]);

  const loadRecent = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const r = await safeFetch(`${BACKEND_URL}/api/v2/agent/sessions?limit=10`, {
        headers: authHeaders(),
      });
      if (!r.ok) return;
      const data = await r.json();
      setRecent(Array.isArray(data?.sessions) ? data.sessions : []);
    } catch {
      /* silent */
    }
  }, [session?.access_token, authHeaders]);

  useEffect(() => { loadRecent(); }, [loadRecent, location.pathname]);

  const userName = user?.name || user?.email?.split('@')[0] || (isArabic ? 'الزائر' : 'Guest');
  const userInitials = (userName || '?').slice(0, 2).toUpperCase();

  const handleNewChat = () => {
    navigate(`/chat?new=${Date.now()}`);
  };

  const openSession = (sid) => {
    if (!sid) return;
    navigate(`/chat#session=${encodeURIComponent(sid)}`);
  };

  const handleRenameCommit = async (sessionId, nextTitle) => {
    // Optimistic update so the row doesn't flicker back to the old name
    // while the network round-trips.
    setRecent((rows) => rows.map((it) => (
      it.session_id === sessionId ? { ...it, preview: nextTitle, custom_title: nextTitle } : it
    )));
    setEditingId(null);
    try {
      const r = await safeFetch(`${BACKEND_URL}/api/v2/agent/sessions/${encodeURIComponent(sessionId)}`, {
        method: 'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: nextTitle }),
      });
      if (!r.ok) {
        // Roll back by re-fetching the source-of-truth list.
        await loadRecent();
      }
    } catch {
      await loadRecent();
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteTarget;
    if (!target?.id) { setDeleteTarget(null); return; }
    setDeleteTarget(null);

    // Snapshot the row so we can re-insert it if the user clicks Undo
    // before the 8s window expires. We don't reload the list from the
    // server during the window — the server has already soft-deleted
    // the row and would return the deleted state.
    const snapshot = recent.find((it) => it.session_id === target.id);
    setRecent((rows) => rows.filter((it) => it.session_id !== target.id));

    let undoClicked = false;

    const performRestore = async () => {
      undoClicked = true;
      try {
        const r = await safeFetch(
          `${BACKEND_URL}/api/v2/agent/sessions/${encodeURIComponent(target.id)}/restore`,
          { method: 'POST', headers: authHeaders() },
        );
        if (r.ok) {
          if (snapshot) {
            setRecent((rows) => (
              rows.some((it) => it.session_id === target.id)
                ? rows
                : [snapshot, ...rows]
            ));
          } else {
            await loadRecent();
          }
        } else {
          await loadRecent();
        }
      } catch {
        await loadRecent();
      }
    };

    try {
      const r = await safeFetch(
        `${BACKEND_URL}/api/v2/agent/sessions/${encodeURIComponent(target.id)}`,
        { method: 'DELETE', headers: authHeaders() },
      );
      if (!r.ok) {
        // Server rejected the delete — put the row back immediately and
        // bail out (no Undo toast).
        if (snapshot) {
          setRecent((rows) => (
            rows.some((it) => it.session_id === target.id) ? rows : [snapshot, ...rows]
          ));
        } else {
          await loadRecent();
        }
        return;
      }
    } catch {
      if (snapshot) {
        setRecent((rows) => (
          rows.some((it) => it.session_id === target.id) ? rows : [snapshot, ...rows]
        ));
      } else {
        await loadRecent();
      }
      return;
    }

    // 8s undo window. After it expires the soft-delete becomes the
    // user's final intent — the row is already gone server-side.
    toast(
      isArabic ? '↩ تم حذف المحادثة' : '↩ Chat deleted',
      {
        description: snapshot?.preview || (isArabic ? '(بدون عنوان)' : '(untitled)'),
        action: {
          label: isArabic ? 'تراجع' : 'Undo',
          onClick: () => { performRestore(); },
        },
        duration: 8000,
        position: 'bottom-center',
        onAutoClose: () => {
          if (!undoClicked) { /* permanent — nothing to do */ }
        },
      },
    );
  };

  // Mobile-only positioning: render the sidebar fixed over the page and
  // slide it in/out via transform. The flex parent reserves zero width
  // for the sidebar on narrow viewports so the main column gets 100%.
  const narrowStyle = isNarrow
    ? {
        position: 'fixed',
        top: 0,
        bottom: 0,
        [isArabic ? 'right' : 'left']: 0,
        zIndex: 50,
        width: '260px',
        transform: mobileOpen
          ? 'translateX(0)'
          : (isArabic ? 'translateX(100%)' : 'translateX(-100%)'),
        transition: 'transform 0.25s ease',
        boxShadow: mobileOpen ? '0 8px 32px rgba(0,0,0,0.30)' : 'none',
      }
    : {};

  return (
    <aside
      dir={isArabic ? 'rtl' : 'ltr'}
      className={cn('h-screen shrink-0 flex flex-col', !isNarrow && 'w-[220px]')}
      style={{
        background: 'var(--sidebar-bg)',
        borderInlineEnd: 'var(--border-sidebar)',
        color: 'var(--text-sidebar-inactive)',
        ...(isNarrow ? { width: 0, overflow: 'visible' } : {}),
      }}
      data-testid="v3-sidebar"
      data-mobile-open={isNarrow ? (mobileOpen ? 'true' : 'false') : undefined}
    >
      <div
        className="h-screen flex flex-col"
        style={{
          width: isNarrow ? '260px' : '220px',
          background: 'var(--sidebar-bg)',
          ...narrowStyle,
        }}
      >
      <button
        type="button"
        onClick={() => navigate('/chat')}
        className="px-5 py-6 flex items-center hover:opacity-75 transition shrink-0"
        data-testid="v3-sidebar-brand"
        aria-label="Bayenah — Home"
      >
        <img
          src="/brand/mark-white.png"
          alt="Bayenah"
          width="28"
          height="28"
          style={{ width: '28px', height: '28px', objectFit: 'contain' }}
          draggable={false}
        />
      </button>

      {/* + محادثة جديدة */}
      <div className="px-3 pb-3 shrink-0">
        <button
          type="button"
          onClick={handleNewChat}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-2 text-[13px] font-medium transition',
            'bg-white text-black hover:bg-white/90',
            isArabic && 'flex-row-reverse',
          )}
          style={{
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '6px',
          }}
          data-testid="v3-sidebar-new-chat"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          <span className="flex-1 text-start">{t.newChat}</span>
        </button>
      </div>

      {/* Scrollable nav */}
      <div className="flex-1 overflow-y-auto pb-2">
        <SectionLabel isArabic={isArabic}>{t.home}</SectionLabel>
        <nav data-testid="v3-sidebar-nav-home">
          <NavItem to="/chat"           icon={MessageSquare} label={t.assistant}    testId="v3-nav-chat"      isArabic={isArabic} />
          <NavItem to="/chat/vault"     icon={FolderOpen}    label={t.vault}        testId="v3-nav-vault"     isArabic={isArabic} />
          <NavItem to="/library"        icon={FileText}      label={t.draftLibrary} testId="v3-nav-library"   isArabic={isArabic} />
        </nav>

        <SectionDivider />

        <SectionLabel isArabic={isArabic}>{t.discover}</SectionLabel>
        <nav data-testid="v3-sidebar-nav-discover">
          <NavItem to="/templates" icon={ClipboardList} label={t.templates} testId="v3-nav-templates" isArabic={isArabic} />
          <NavItem to="/guidance"  icon={BookOpen}      label={t.guidance}  testId="v3-nav-guidance"  isArabic={isArabic} />
        </nav>

        <SectionDivider />

        <SectionLabel isArabic={isArabic}>{t.recentChats}</SectionLabel>
        {recent.length === 0 ? (
          <div
            className={cn('px-5 py-1', isArabic && 'text-right')}
            style={{ fontSize: '11px', color: 'rgba(255,255,255,0.20)', fontStyle: 'italic' }}
          >
            {isArabic ? 'لا توجد محادثات بعد' : 'No conversations yet'}
          </div>
        ) : (
          <ul data-testid="v3-sidebar-recent">
            {recent.map((it) => (
              <RecentRow
                key={it.session_id}
                item={it}
                isArabic={isArabic}
                onOpen={openSession}
                onRequestRename={(sid) => setEditingId(sid)}
                onRequestDelete={(id, label) => setDeleteTarget({ id, label })}
                isEditing={editingId === it.session_id}
                onEditCommit={(next) => handleRenameCommit(it.session_id, next)}
                onEditCancel={() => setEditingId(null)}
              />
            ))}
          </ul>
        )}
      </div>

      {/* Cluster B-1 (Feb 06 2026): Phase-2 annotation queue. Always
        * visible between recents and settings per the discoverability
        * decision (Q4-a). Header shows live count badge; empty state
        * tells the user how to seed it. Gated on an access token so
        * guests don't see a half-rendered panel. */}
      {session?.access_token ? (
        <div
          className="px-2 pb-2"
          style={{ borderTop: 'var(--border-sidebar)' }}
          data-testid="v3-sidebar-queue-section"
        >
          <QueueSection accessToken={session.access_token} isArabic={isArabic} />
        </div>
      ) : null}

      {/* Footer: settings + user identity */}
      <div className="px-2 py-2" style={{ borderTop: 'var(--border-sidebar)' }}>
        <NavItem
          to="/settings"
          icon={SettingsIcon}
          label={t.settings}
          testId="v3-nav-settings"
          isArabic={isArabic}
        />
        <div className={cn('mt-2 px-3 py-2 flex items-center gap-3', isArabic && 'flex-row-reverse')}>
          <div
            aria-hidden="true"
            className="w-[30px] h-[30px] flex items-center justify-center text-[11px] font-medium text-white shrink-0"
            style={{ background: '#111111', borderRadius: 'var(--radius-avatar)' }}
          >
            {userInitials}
          </div>
          <div className={cn('flex-1 min-w-0', isArabic && 'text-right')}>
            <div className="text-[12px] text-white truncate" data-testid="v3-sidebar-user-name">
              {userName}
            </div>
            <div
              className="text-[10px] truncate"
              style={{ color: 'rgba(255,255,255,0.4)' }}
              data-testid="v3-sidebar-user-email"
            >
              {user?.email || ''}
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              // Cluster A (iter 2): manual tab-local lock for
              // shared-workstation users. Same semantics as the
              // idle timeout: clear this tab's React state + set
              // the lock flag + redirect to /signin?idle=1 (Phase-1
              // retarget — the static landing at / does not carry
              // the Resume banner), leaving Supabase storage intact
              // so sibling tabs / devices stay signed in and Resume
              // needs no password.
              try { sessionStorage.removeItem('bayenah_last_active'); } catch (_e) { /* ignore */ }
              try { sessionStorage.setItem('bayenah_tab_locked', '1'); } catch (_e) { /* ignore */ }
              window.location.replace('/signin?idle=1');
            }}
            className="hover:text-white transition"
            style={{ color: 'rgba(255,255,255,0.4)' }}
            aria-label={isArabic ? 'قفل هذا التبويب' : 'Lock this tab'}
            title={isArabic ? 'قفل هذا التبويب' : 'Lock this tab'}
            data-testid="v3-sidebar-lock-tab"
          >
            <Lock className="w-4 h-4" strokeWidth={1.7} />
          </button>
          <button
            type="button"
            onClick={logout}
            className="hover:text-white transition"
            style={{ color: 'rgba(255,255,255,0.4)' }}
            aria-label={t.signOut}
            data-testid="v3-sidebar-sign-out"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.7} />
          </button>
        </div>
      </div>
      </div>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
      >
        <AlertDialogContent
          dir={isArabic ? 'rtl' : 'ltr'}
          data-testid="v3-sidebar-delete-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className={isArabic ? 'text-right' : 'text-left'}>
              {isArabic ? 'حذف المحادثة؟' : 'Delete this conversation?'}
            </AlertDialogTitle>
            <AlertDialogDescription className={isArabic ? 'text-right' : 'text-left'}>
              {isArabic
                ? `سيتم حذف "${deleteTarget?.label || ''}" من قائمة المحادثات. لا يمكن التراجع عن هذا الإجراء.`
                : `"${deleteTarget?.label || ''}" will be removed from your recent conversations. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="v3-sidebar-delete-cancel">
              {isArabic ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white"
              data-testid="v3-sidebar-delete-confirm"
            >
              {isArabic ? 'حذف' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}

export default Sidebar;
